import { useAppConfigContext } from "@/context/AppConfig";
import { useReportContext } from "@/context/ReportContext";
import { ListForm } from "@/reports/sections/ListsSection";
import { ListMenuItem } from "@/reports/sections/ListsSection/ListsSections/ListMenuItem";
import {
  Button,
  Divider,
  ListItemIcon,
  MenuItem as MUIMenuItem,
  Menu,
  Text,
} from "@/theme/components";
import { Icons, type SxProps } from "@/theme/index";
import { useRedirect } from "@/utils/hooks/useRedirect";
import { useState } from "react";
import { ReportActionButton } from "../ReportActionButton/ReportActionButton";
import { useListAction } from "./useListAction";

type ListActionProps = {
  reportTitle: string;
  reportPermalink?: string;
  disableCollapse?: boolean;
  sx?: SxProps;
  onReportCard?: boolean;
};

type MenuItem = {
  icon?: JSX.Element;
  display: string;
  action: () => void;
};

export const ListAction: React.FC<ListActionProps> = ({
  reportTitle,
  reportPermalink,
  disableCollapse,
  sx,
  onReportCard,
}) => {
  const { trackEvent } = useAppConfigContext();
  const { redirect } = useRedirect();
  const { permalink: contextPermalink } = useReportContext();
  const permalink = reportPermalink || contextPermalink;
  const { createList, lists, foundInList } = useListAction();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [listFormOpen, setListFormOpen] = useState(false);
  const active = foundInList;

  const menuActions = [
    {
      display: "Add to new list",
      action: () => {
        trackEvent(
          "add to list entry point",
          "engagement click",
          "rats component",
        );
        setListFormOpen(true);
      },
      icon: <Icons.Add fontSize="small" color="primary" />,
    },
    {
      display: "Manage saved reports and lists",
      action: () => {
        redirect("/dashboard/lists");
      },
      icon: <Icons.Settings fontSize="small" color="primary" />,
    },
  ];

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    trackEvent("Lists", "engagement click", "rats component");
    setAnchorEl(event.currentTarget);
    setListFormOpen(false);
  };

  const handleCloseMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleCloseItem = (
    event: React.MouseEvent<HTMLButtonElement>,
    clickedItem: MenuItem,
  ) => {
    event.stopPropagation();
    clickedItem.action();
  };

  return (
    <>
      <ReportActionButton
        aria-controls="basic-menu"
        aria-haspopup="true"
        aria-expanded="true"
        collapseText="List"
        iconName="List"
        onClick={handleClick}
        active={active}
        disableCollapse={disableCollapse}
        sx={sx}
        onReportCard={onReportCard}
      />
      <Menu
        id="list-basic-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleCloseMenu}
      >
        {listFormOpen ? (
          <ListForm
            closeForm={() => setAnchorEl(null)}
            reportTitle={reportTitle}
            permalink={permalink}
            title="Create new list"
            mainActionButtonText="Create list"
            placeholderText="List name"
            action={createList}
            key={permalink}
          />
        ) : (
          <div key="list-menu">
            {menuActions.map((item) => (
              <MUIMenuItem
                data-testid="list-actions-menu"
                key={`menu-action-${item.display}`}
              >
                <Button onClick={(event) => handleCloseItem(event, item)}>
                  {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                  <Text color="primary">{item.display}</Text>
                </Button>
              </MUIMenuItem>
            ))}
            <Divider sx={{ width: "87%", margin: "12px auto" }} />
            {lists?.map((list) => {
              return (
                <ListMenuItem
                  key={list.id}
                  list={list}
                  reportPermaLink={permalink}
                />
              );
            })}
          </div>
        )}
      </Menu>
    </>
  );
};
